/**
 * Ribbon data extraction: a port of RenderingViewModel.ComputeRibbonRenderData and its helpers in
 * vendor-ref/nanome/Assets/Scripts/src/Nanome.Adapter/Display/RenderingViewModel.Ribbon.cs
 * (:24 GapDistance, :458-828 extraction, :829-837 TrimEndGaps, :839-865 SplitWithGaps,
 *  :867-929 RibbonSequenceId, :931-943 SecondaryStructureToCartoonStyle).
 *
 * Differences from the C#, all deliberate:
 * - Residue.Index is the model residue index (the contract), not Nanome's running counter.
 * - Residues are identified by model residue index; two model residues that share (chain, serial, insCode) are
 *   kept apart (Nanome merges their atoms). They compare equal, so a gap is placed between them.
 * - A guanine without N1 (or a non-guanine without N3) falls back to the other nitrogen; Nanome would throw.
 * - Coordinates stay in the model frame (Å); Nanome's y/z swap is the renderer's job (see builder.ts).
 */
import { isNucleic, isProtein, isPolymer } from '@nanome-lite/data/model';
import { RIBBON, SecondaryStructure } from '@nanome-lite/look';
import { CartoonStyle, type CartoonStyleType, type RibbonSegment, type RibbonSourceModel } from './types';
import { PURINE_RING_FLOATS, PYRIMIDINE_RING_FLOATS } from './builder';

/** Nanome's InsertionCode.Empty is ' ' (InsertionCode.cs:12); the model stores '' when none. */
const EMPTY_INSERTION_CODE = 32;

/** RenderingViewModel.Ribbon.cs:931-943 */
export function secondaryStructureToCartoonStyle(ss: number): CartoonStyleType {
  switch (ss) {
    case SecondaryStructure.Helix3: return CartoonStyle.Helix3;
    case SecondaryStructure.Helix5: return CartoonStyle.Helix5;
    case SecondaryStructure.AlphaHelix: return CartoonStyle.Helix;
    case SecondaryStructure.Nucleic_Helix: return CartoonStyle.NucleicHelix;
    case SecondaryStructure.Strand: return CartoonStyle.Sheet;
    case SecondaryStructure.BetaBridge: return CartoonStyle.Sheet;
    default: return CartoonStyle.Coil;
  }
}

/** The (chain, serial, insCode) key of RibbonSequenceId (RenderingViewModel.Ribbon.cs:867-878). */
export interface SequenceId { chain: string; serial: number; insCode: number }

export function sequenceId(model: RibbonSourceModel, residue: number): SequenceId {
  const r = model.residues;
  const ins = r.insCode[residue] ?? '';
  return {
    chain: model.chains.name[r.chainIndex[residue]!] ?? '',
    serial: r.serial[residue]!,
    insCode: ins.length > 0 ? ins.charCodeAt(0) : EMPTY_INSERTION_CODE,
  };
}

/** ChainName.CompareTo: string.Compare(a, b, StringComparison.OrdinalIgnoreCase) (ChainName.cs:26-29). */
function compareChainNames(a: string, b: string): number {
  if (a === b) return 0;
  const ua = a.toUpperCase(), ub = b.toUpperCase();
  const n = Math.min(ua.length, ub.length);
  for (let i = 0; i < n; i++) {
    const d = ua.charCodeAt(i) - ub.charCodeAt(i);
    if (d !== 0) return d;
  }
  return ua.length - ub.length;
}

/** RibbonSequenceId.CompareTo (RenderingViewModel.Ribbon.cs:913-928) */
export function compareSequenceIds(a: SequenceId, b: SequenceId): number {
  if (a.chain !== b.chain) {
    const c = compareChainNames(a.chain, b.chain);
    if (c !== 0) return c;
  }
  return a.serial === b.serial ? a.insCode - b.insCode : a.serial - b.serial;
}

/** RibbonSequenceId.IsContinuous (RenderingViewModel.Ribbon.cs:880-894): same chain, serial +1 (or -1 -> 1), or same serial and insertion code char +1. */
export function isContinuous(a: SequenceId, b: SequenceId): boolean {
  if (a.chain !== b.chain) return false;
  if (a.serial === b.serial) return b.insCode - a.insCode === 1;
  return b.serial - a.serial === 1 || (a.serial === -1 && b.serial === 1);
}

interface Draft {
  residue: number;
  gap: boolean;
  style: CartoonStyleType;
  p1: [number, number, number];
  p2: [number, number, number];
  p3: [number, number, number];
  color: [number, number, number];
  /** base ring atoms, flat xyz: 33 floats purine, 18 pyrimidine, 0 none (layout documented in builder.ts) */
  ring: Float32Array;
}

const ZERO: [number, number, number] = [0, 0, 0];
const NO_RING = new Float32Array(0);

function gapDraft(residue: number): Draft {
  return { residue, gap: true, style: CartoonStyle.Coil, p1: ZERO, p2: ZERO, p3: ZERO, color: ZERO, ring: NO_RING };
}

/** RenderingViewModel.Ribbon.cs:829-837 */
function trimEndGaps(chain: Draft[]): void {
  while (chain.length > 0 && chain[chain.length - 1]!.gap) chain.pop();
}

/** RenderingViewModel.Ribbon.cs:839-865: gap residues are dropped and split the run. */
function splitWithGaps(segments: Draft[][]): Draft[][] {
  const result: Draft[][] = [];
  for (const segment of segments) {
    let current: Draft[] = [];
    for (const residue of segment) {
      if (residue.gap) {
        if (current.length > 0) { result.push(current); current = []; }
        continue;
      }
      current.push(residue);
    }
    if (current.length > 0) result.push(current);
  }
  return result;
}

function toSegment(drafts: Draft[]): RibbonSegment {
  const n = drafts.length;
  const residueIndex = new Uint32Array(n);
  const p1 = new Float32Array(3 * n), p2 = new Float32Array(3 * n), p3 = new Float32Array(3 * n);
  const style = new Uint8Array(n);
  const color = new Float32Array(3 * n);
  let nucleic = false;
  for (let i = 0; i < n; i++) {
    const d = drafts[i]!;
    residueIndex[i] = d.residue;
    p1.set(d.p1, 3 * i); p2.set(d.p2, 3 * i); p3.set(d.p3, 3 * i);
    style[i] = d.style;
    color.set(d.color, 3 * i);
    if (d.style === CartoonStyle.NucleicHelix) nucleic = true; // RibbonBuilder.cs:298
  }
  const segment: RibbonSegment = { residueIndex, p1, p2, p3, style, color, nucleic };
  if (nucleic) segment.baseRings = drafts.map((d) => d.ring);
  return segment;
}

/**
 * Extract the continuous polymer segments a component's ribbon is built from.
 * @param model the entry's model (current frame coordinates)
 * @param atomIndices the component's atom set (sorted, from evaluateFilters) or null for every atom; residues with no
 *   atom in the set are absent, residues whose trace atoms are outside it become gaps, as in Nanome where only the
 *   component's AtomReferences reach ComputeRibbonRenderData.
 * @param colorPerResidue colour of the residue's trace atom (Cα / C5') under the component's coloring, 0..1 floats.
 */
export function extractSegments(
  model: RibbonSourceModel,
  atomIndices: Uint32Array | null,
  colorPerResidue: (residueIndex: number) => [number, number, number],
): RibbonSegment[] {
  const res = model.residues;
  const xyz = model.xyz;
  const names = model.atomName;

  let mask: Uint8Array | null = null;
  if (atomIndices) {
    mask = new Uint8Array(model.atomCount);
    for (let i = 0; i < atomIndices.length; i++) mask[atomIndices[i]!] = 1;
  }

  // Residue groups: polymer residues with at least one atom in the selection (RenderingViewModel.Ribbon.cs:499-537),
  // sorted by (chain, serial, insCode) (:538) with the model order as a stable tiebreak.
  const order: number[] = [];
  for (let r = 0; r < res.count; r++) {
    if (!isPolymer(res.substructure[r]!)) continue;
    if (mask) {
      const start = res.atomStart[r]!, end = start + res.atomCount[r]!;
      let any = false;
      for (let a = start; a < end; a++) if (mask[a]) { any = true; break; }
      if (!any) continue;
    }
    order.push(r);
  }
  const keys = new Map<number, SequenceId>();
  for (const r of order) keys.set(r, sequenceId(model, r));
  order.sort((a, b) => compareSequenceIds(keys.get(a)!, keys.get(b)!) || a - b);

  const segmentsList: Draft[][] = [];
  let chain: Draft[] = [];
  let currentChain = -1;
  let lastKey: SequenceId | null = null;
  const pos = (a: number): [number, number, number] => [xyz[3 * a]!, xyz[3 * a + 1]!, xyz[3 * a + 2]!];

  for (const r of order) {
    const key = keys.get(r)!;
    const sub = res.substructure[r]!;
    const protein = isProtein(sub);
    const nucleic = !protein && isNucleic(sub);
    const chainIdx = res.chainIndex[r]!;

    // New chain (:563-572)
    if (currentChain !== chainIdx && chain.length !== 0) {
      trimEndGaps(chain);
      if (chain.length !== 0) { segmentsList.push(chain); chain = []; }
    }

    const start = res.atomStart[r]!, end = start + res.atomCount[r]!;

    if (protein) {
      // first CA and first O, by standard name (AtomName.cs:48,50; names are upper-cased on construction :16)
      let ca = -1, o = -1;
      for (let a = start; a < end; a++) {
        if (mask && !mask[a]) continue;
        const name = names[a]!.toUpperCase();
        if (ca < 0 && name === 'CA') ca = a;
        else if (o < 0 && name === 'O') o = a;
        if (ca >= 0 && o >= 0) break;
      }
      let current: Draft;
      if (ca < 0 || o < 0) {
        current = gapDraft(r); // :691-694
      } else {
        current = {
          residue: r, gap: false,
          style: secondaryStructureToCartoonStyle(res.ss[r]!), // :700
          p1: pos(ca), p2: pos(o), p3: ZERO,                    // :701-702
          color: colorPerResidue(r),                            // :698
          ring: NO_RING,
        };
        if (chain.length > 0) {
          const last = chain[chain.length - 1]!;
          if (!isContinuous(lastKey!, key)) {
            // :707-712: a copy of the previous residue flagged as a gap is appended as a split marker
            chain.push({ ...last, gap: true });
          } else if (!last.gap) {
            // :713-717: the residue after a long Cα–Cα jump is itself flagged as a gap (and so dropped by splitWithGaps)
            const dx = last.p1[0] - current.p1[0], dy = last.p1[1] - current.p1[1], dz = last.p1[2] - current.p1[2];
            current.gap = Math.sqrt(dx * dx + dy * dy + dz * dz) >= RIBBON.gapDistance;
          }
        }
      }
      chain.push(current); // :721
    } else if (nucleic) {
      // first occurrence of each named atom (:612-687)
      let c5p = -1, c3p = -1, o3p = -1, n1 = -1, n3 = -1, n7 = -1, n9 = -1, c2 = -1, c4 = -1, c5 = -1, c6 = -1, c8 = -1;
      for (let a = start; a < end; a++) {
        if (mask && !mask[a]) continue;
        switch (names[a]!.toUpperCase()) {
          case "C5'": if (c5p < 0) c5p = a; break;
          case "C3'": if (c3p < 0) c3p = a; break;
          case "O3'": if (o3p < 0) o3p = a; break;
          case 'N1': if (n1 < 0) n1 = a; break;
          case 'N3': if (n3 < 0) n3 = a; break;
          case 'N7': if (n7 < 0) n7 = a; break;
          case 'N9': if (n9 < 0) n9 = a; break;
          case 'C2': if (c2 < 0) c2 = a; break;
          case 'C4': if (c4 < 0) c4 = a; break;
          case 'C5': if (c5 < 0) c5 = a; break;
          case 'C6': if (c6 < 0) c6 = a; break;
          case 'C8': if (c8 < 0) c8 = a; break;
        }
      }
      let current: Draft;
      if (c3p >= 0 && c5p >= 0 && (n1 >= 0 || n3 >= 0)) { // :725
        const resName = res.name[r]!;
        const isGuanine = resName === 'DG' || resName === 'G';    // :731
        const isAdenine = resName === 'DA' || resName === 'A';    // :732
        const isCytosine = resName === 'DC' || resName === 'C';   // :733
        const isThymine = resName === 'DT' || resName === 'T';    // :734
        const isUracil = resName === 'U';                         // :735
        // :737 picks N1 for guanine, N3 otherwise; fall back to whichever exists instead of throwing
        let nitrogen = isGuanine ? n1 : n3;
        if (nitrogen < 0) nitrogen = isGuanine ? n3 : n1;

        let ring = NO_RING;
        if (o3p >= 0) { // :747
          if ((isGuanine || isAdenine) &&
            n9 >= 0 && c4 >= 0 && c5 >= 0 && n7 >= 0 && c8 >= 0 && n3 >= 0 && c2 >= 0 && n1 >= 0 && c6 >= 0) { // :749-752
            ring = new Float32Array(PURINE_RING_FLOATS);
            const atoms = [n9, c4, c5, n7, c8, /* ring2 */ c4, n3, c2, n1, c6, c5]; // :754-770
            for (let i = 0; i < atoms.length; i++) ring.set(pos(atoms[i]!), 3 * i);
          }
          if ((isCytosine || isThymine || isUracil) &&
            n1 >= 0 && n3 >= 0 && c2 >= 0 && c4 >= 0 && c5 >= 0 && c6 >= 0) { // :774-776
            ring = new Float32Array(PYRIMIDINE_RING_FLOATS);
            const atoms = [n1, c2, n3, c4, c5, c6]; // :778-786
            for (let i = 0; i < atoms.length; i++) ring.set(pos(atoms[i]!), 3 * i);
          }
        }
        current = {
          residue: r, gap: false,
          style: CartoonStyle.NucleicHelix,                     // :741
          p1: pos(c5p), p2: pos(c3p), p3: pos(nitrogen),        // :742-744
          color: colorPerResidue(r),                            // :739
          ring,
        };
        // :791-797: a non-consecutive id flags the current residue itself (no split marker, no distance check)
        if (chain.length > 0 && !isContinuous(lastKey!, key)) current.gap = true;
      } else {
        current = gapDraft(r); // :799-802
      }
      // "Do not start the chain with gaps" (:804-808)
      if (chain.length !== 0 || !current.gap) chain.push(current);
    }

    currentChain = chainIdx;
    lastKey = key;
  }

  trimEndGaps(chain);
  if (chain.length > 0) segmentsList.push(chain); // :817-821

  return splitWithGaps(segmentsList).map(toSegment); // :824
}
